/**
 * STIEN EN SIDE LIGGER I, når menyen ikke kan svare på det alene.
 *
 * ================================================================================================
 * Atle, 11. september, med et skjermbilde: «Stien til denne skal jo da være standing Ovation >
 * artister > Bendik > Demo advance eller noe sånt.»
 *
 * Toppen kunne to nivåer: organisasjonen, og navnet på menypunktet ruta hører til. Det holder for
 * en liste — «Standing Ovation › Artister» — og slutter å holde i det man går ned i noe: en artist,
 * og så en advance under den. Da sto det fortsatt bare «Artister», og siden fortalte deg hvor du
 * var med to av fire ledd.
 *
 * ================================================================================================
 * SIDEN SIER DET, IKKE RUTA.
 *
 * Leddene er DATA — artistens navn, ikke «artist» — og det finnes bare ett sted i koden der navnet
 * er hentet: siden som viser henne. En `staticData`-liste på ruta kunne bare bære oversettelses-
 * nøkler, og ville skrevet «Artister › Artist › Advance».
 *
 * Derfor melder siden fra med `useBreadcrumbTrail`, og toppen tegner det den får. Sier ingen fra,
 * faller den tilbake på menypunktet som før — hver eneste side som fantes i går ser lik ut i dag.
 *
 * ================================================================================================
 * TO KONTEKSTER, OG DET ER IKKE OVERDREVET.
 *
 * Setteren og verdien bor hver for seg fordi de har helt forskjellig levetid. Med begge i én ville
 * `useMemo`-objektet endret identitet hver gang stien ble satt — og effekten under, som avhenger av
 * det, ville kjørt på nytt, ryddet, satt på nytt, og løpt i ring til nettleseren ga opp.
 *
 * `useState`-setteren har stabil identitet gjennom hele levetiden. Den er derfor trygg å avhenge
 * av; verdien er det ikke.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

/** Ett ledd. `to` gjør det klikkbart; uten den er det stedet man ER, og det skal man ikke klikke. */
export interface Crumb {
  readonly label: string;
  readonly to?: string;
}

const TrailContext = createContext<readonly Crumb[]>([]);
const SetTrailContext = createContext<Dispatch<SetStateAction<readonly Crumb[]>> | null>(null);

/*
 * SIDENS NAVN NÅR RUTA IKKE KAN VITE DET (310.12). Den samme siden viser en programpost på en festival
 * og artistens konsert på et spillested; ruta kan bare bære én nøkkel, og «Programpost» er feil for
 * artisten som åpner konserten sin. Siden melder fra om navnet, som om stien.
 */
const TitleContext = createContext<string | null>(null);
const SetTitleContext = createContext<Dispatch<SetStateAction<string | null>> | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }): ReactNode {
  const [trail, setTrail] = useState<readonly Crumb[]>([]);
  const [title, setTitle] = useState<string | null>(null);
  return (
    <SetTrailContext.Provider value={setTrail}>
      <SetTitleContext.Provider value={setTitle}>
        <TitleContext.Provider value={title}>
          <TrailContext.Provider value={trail}>{children}</TrailContext.Provider>
        </TitleContext.Provider>
      </SetTitleContext.Provider>
    </SetTrailContext.Provider>
  );
}

/** The name the page gave itself, or null for the route's own. */
export function usePageTitle(): string | null {
  return useContext(TitleContext);
}

/** A page names itself; the name goes when the page does. Null leaves the route's name. */
export function usePageTitleOverride(title: string | null): void {
  const setTitle = useContext(SetTitleContext);
  useEffect(() => {
    if (setTitle === null) return;
    setTitle(title);
    return () => {
      setTitle(null);
    };
  }, [title, setTitle]);
}

/** Leddene toppen skal tegne. Tom liste betyr «bruk menypunktet», som før. */
export function useBreadcrumbs(): readonly Crumb[] {
  return useContext(TrailContext);
}

/**
 * En side melder fra om hvor den ligger.
 *
 * RYDDER ETTER SEG NÅR DEN FORLATES. Uten det ville stien blitt stående på neste side, og en
 * brødsmule som lyver om hvor du er er verre enn ingen brødsmule — det var nettopp den feilen
 * toppen ble skrevet om for 28. august.
 *
 * `JSON.stringify` som avhengighet framfor selve lista: en side som bygger leddene i render ville
 * ellers meldt fra i en evig løkke, fordi et nytt array aldri er likt det forrige.
 */
export function useBreadcrumbTrail(trail: readonly Crumb[]): void {
  const setTrail = useContext(SetTrailContext);
  const key = JSON.stringify(trail);

  useEffect(() => {
    if (setTrail === null) return;
    setTrail(JSON.parse(key) as Crumb[]);
    return () => {
      setTrail([]);
    };
  }, [key, setTrail]);
}
